import React, { useMemo, useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import { SwiperSlide } from 'swiper/react';

import { Carousel, CarouselWrapper, CarouselPagination } from '$partials/base/carousel.jsx';
import { ExchangeRateProvider } from '$contexts/exchange-rate';
import { Footer, Header } from '$partials/global';
import { GetInTouchBlock } from '$partials/modules/blocks';
import { formatPropertyId, groupPropertyModulesStream } from '$utilities/data';
import { PropertyInfoCard } from '$partials/properties/index.js';
import { PropertyProvider } from '$contexts/property';
import Button from '$partials/ui/button';
import getGoogleMapsKey from '$utilities/google-maps-key';
import IconArrow from '$partials/icons/icon-arrow';
import IconClose from '$partials/icons/icon-close';
import IconGallery from '$partials/icons/icon-gallery.jsx';
import IconMarker from '$partials/icons/icon-marker.jsx';
import MediaGallery, { MediaGalleryProvider, MediaGalleryContext } from '$partials/global/media-gallery.jsx';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import NewHomePropertyList from '$partials/new-home/new-home-property-list';
import SimpleImage from '$partials/media/image-simple.jsx';
import TextAccordion from '$partials/modules/blocks/text-accordion.jsx';
import { AreaUnitsProvider } from '$contexts/area-units';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').NewHomeDetailPage}
 */
const NewHomeDetailPage = (props) => {
    const page = props.data.page;

    const carouselRef = useRef(null);
    const [cursorPos, setCursorPos] = useState({ x: 0,
        y: 0,
        visible: false });
    const [showMap, setShowMap] = useState(false);
    const [showAllModules, setShowAllModules] = useState(page.modules?.length > 0 ? false : true);

    const handleMouseMove = (e) => {
        const rect = carouselRef.current?.getBoundingClientRect();
        if (!rect) { return; }
        // position the 40x40 box so its bottom-right corner is at the cursor
        const x = e.clientX - rect.left - 40;
        const y = e.clientY - rect.top - 40;
        setCursorPos({ x,
            y,
            visible: true });
    };

    const handleMouseLeave = () => setCursorPos((s) => ({ ...s,
        visible: false }));

    const modules = useMemo(() => groupPropertyModulesStream(page.modules), [page?.modules]);
    const mediaItems = useMemo(() => page.modules?.filter(item => item.__typename === 'PropertyDetailSectionMediaItem'), [page?.modules]);
    const propertyReferenceId = formatPropertyId(page.externalId, '');

    return (
        <>
            <AreaUnitsProvider>
                <PropertyProvider property={{
                    externalId: page.externalId,
                    office: page.office,
                    primaryAgent: page.primaryAgent,
                    propertyReferenceId: propertyReferenceId,
                    pageTitle: page.title,
                    pageUrl: props?.location
                }}>
                    <ExchangeRateProvider
                        defaultCurrency={page.priceCurrency}
                        exchangeRate={props.data?.exchangeRates}>
                        <Header
                            {...props.data.header}
                            variant='PDP' />

                        <main
                            id='main-content'>
                            {/* Property details */}
                            <MediaGalleryProvider>
                                <MediaGalleryContext.Consumer>
                                    {({ setGalleryInitialId, galleryInitialId }) => (
                                        <>
                                            <div
                                                className='relative cursor-pointer aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] w-full'
                                                onMouseEnter={() =>
                                                    setCursorPos((s) => ({
                                                        ...s,
                                                        visible: true
                                                    }))
                                                }
                                                onMouseLeave={handleMouseLeave}
                                                onMouseMove={handleMouseMove}
                                                ref={carouselRef}>
                                                {!showMap ? (
                                                    <Carousel
                                                        swiperConfig={{
                                                            a11y: { enabled: true },
                                                            slidesPerView: 1,
                                                            grabCursor: false,
                                                        }}>
                                                        <CarouselWrapper>
                                                            {page?.images?.map((item) => {
                                                                const video = item.media.video || item.media.videoEmbed;
                                                                const hasVideo = !!video?.src;
                                                                return (
                                                                    <SwiperSlide
                                                                        aria-roledescription='slide'
                                                                        className='select-none cursor-pointer w-full'
                                                                        key={item.id}
                                                                        onClick={() =>
                                                                            setGalleryInitialId(item.id)
                                                                        }
                                                                        role='group'>
                                                                        {(hasVideo && video.poster?.url) &&
                                                                            <SimpleImage
                                                                                className='w-full aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] object-cover'
                                                                                src={video.poster.url} />
                                                                        }
                                                                        {!hasVideo &&
                                                                            <SimpleImage
                                                                                className='w-full aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] object-cover'
                                                                                {...item.media?.image} />
                                                                        }
                                                                    </SwiperSlide>
                                                                );
                                                            })}
                                                        </CarouselWrapper>

                                                        {page?.images?.length > 1 && (
                                                            <CarouselPagination totalSlides={page.images.length} />
                                                        )}
                                                    </Carousel>
                                                ) : (
                                                    <>
                                                        <Button
                                                            className='hidden md:absolute z-20 top-24 right-24 md:flex select-none h-[40px] w-[40px] rounded justify-center items-center button-secondary-filled'
                                                            onClick={() => setShowMap(false)}>
                                                            <IconClose />
                                                        </Button>

                                                        <GoogleMapReact
                                                            yesIWantToUseGoogleMapApiInternals
                                                            bootstrapURLKeys={{
                                                                key: getGoogleMapsKey(),
                                                            }}
                                                            defaultCenter={{
                                                                lat: page?.coordinates?.lat,
                                                                lng: page?.coordinates?.lng,
                                                            }}
                                                            defaultZoom={16}
                                                            options={{
                                                                mapId: 'ca9052da21cdf8f0b6ba14d4',
                                                                disableDefaultUI: true,
                                                                keyboardShortcuts: false,
                                                            }}>
                                                            <IconMarker
                                                                lat={page?.coordinates?.lat}
                                                                lng={page?.coordinates?.lng}/>
                                                        </GoogleMapReact>
                                                    </>
                                                )}

                                                {!showMap && (
                                                    <div
                                                        aria-hidden
                                                        className='absolute left-0 top-0 h-40 w-40 rounded bg-grey-grey-200 pointer-events-none flex items-center justify-center'
                                                        style={{
                                                            transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`,
                                                            transition:
                                                    'transform 0.04s linear, opacity 0.08s linear',
                                                            opacity: cursorPos.visible ? 1 : 0,
                                                            zIndex: 40,
                                                        }}>
                                                        <IconGallery fill='#2c2b2aff' />
                                                    </div>
                                                )}
                                            </div>

                                            {galleryInitialId && page?.images?.length > 0 && (
                                                <MediaGallery
                                                    initialId={galleryInitialId}
                                                    items={page.images}/>
                                            )}
                                        </>
                                    )}
                                </MediaGalleryContext.Consumer>
                                <PropertyInfoCard
                                    {...page}
                                    __typename='SalePropertyDetailPage'
                                    bathrooms={page.noBaths}
                                    bedrooms={page.noBaths}
                                    livingAreaMeters={page.buildSize}
                                    outdoorAreaMeters={page.landSize}
                                    price={page.price}
                                    priceTypeLabel='Prices from'
                                    propertiesCount={page.noProperties}
                                    propertyReferenceId={propertyReferenceId}
                                    setShowMap={(page.coordinates?.lat && page.coordinates?.lng) ? setShowMap : null}
                                    title={page.title || ''} />
                            </MediaGalleryProvider>

                            {page.description && (
                                <section>
                                    <div className='theme-light bg-main-background py-24 sm:py-40 md:py-48'>
                                        <div className='layout-container layout-site-outer-padding'>
                                            <div className='col-wrapper flex'>
                                                <div
                                                    className='type-style-body-l mt-16 md:w-8/12 col-padding rich-text'
                                                    dangerouslySetInnerHTML={{ __html: page.description }} />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}
                            {(page?.highlights?.length > 0 || page?.newHomeAmenities?.length > 0) && (
                                <section>
                                    <TextAccordion
                                        col1Heading='Highlights'
                                        col1Items={page?.highlights}
                                        col2Heading='Amenities'
                                        col2Items={page?.newHomeAmenities}
                                        heading='Key Details'/>
                                </section>
                            )}

                            <MediaGalleryProvider>
                                <div
                                    className={`${showAllModules ? '' : 'max-h-[550px] md:max-h-[700px] overflow-y-clip relative'}`}>

                                    {!showAllModules && (
                                        <>
                                            <div
                                                className='absolute inset-0 z-10 h-full w-full'
                                                style={{
                                                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 82.22%)'
                                                }} />
                                            <div className='absolute z-20 inset-x-0 bottom-0 pb-32 w-full flex justify-center'>
                                                <button
                                                    aria-label='Open additional content sections'
                                                    className='type-style-eyebrow inline-flex gap-8 items-center'
                                                    onClick={() => setShowAllModules(true)}
                                                    type='button'>
                                                    {'See more'}
                                                    <span aria-hidden={true}>
                                                        <IconArrow className='rotate-90 h-10' />
                                                    </span>
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    <div inert={!showAllModules}>
                                        {modules?.length > 0 && (
                                            <section
                                                className='theme-light-alt'>
                                                {modules.map((module, i) => (
                                                    <ModuleDispatcher
                                                        {...module}
                                                        key={i + module?.id}/>
                                                ))}
                                            </section>
                                        )}
                                    </div>
                                </div>
                                {/* Available properties */}
                                <section>
                                    <NewHomePropertyList properties={page.properties}/>
                                </section>
                                <section className='theme-light-alt'>
                                    <GetInTouchBlock />
                                </section>
                                <MediaGalleryContext.Consumer>
                                    {({ galleryInitialId }) => {
                                        return (
                                            galleryInitialId && mediaItems?.length > 0 && (
                                                <MediaGallery
                                                    initialId={galleryInitialId}
                                                    items={mediaItems} />
                                            )
                                        );
                                    }}
                                </MediaGalleryContext.Consumer>
                            </MediaGalleryProvider>
                        </main>

                        <Footer {...props.data.footer} />
                    </ExchangeRateProvider>
                </PropertyProvider>
            </AreaUnitsProvider>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default NewHomeDetailPage;
